import propTypes from 'prop-types';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';

export const PricingPlan = (props) => {
  const {
    onClick,
    cta,
    currency,
    code,
    image,
    name,
    price,
    sx,
    ...other
  } = props;

  const displayCode = code
    ? (String(code).startsWith('#') ? code : `#${code}`)
    : null;

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick?.();
    }
  };

  return (
    <Card
      aria-label={name ? `View ${name}` : undefined}
      elevation={0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role="button"
      sx={{
        bgcolor: 'background.paper',
        border: (theme) => theme.palette.mode === 'dark'
          ? `1px solid ${theme.palette.neutral[700]}`
          : `1px solid ${theme.palette.neutral[200]}`,
        borderRadius: 1,
        boxSizing: 'border-box',
        cursor: onClick ? 'pointer' : 'default',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxWidth: '100%',
        outline: 'none',
        overflow: 'hidden',
        position: 'relative',
        transition: 'border-color 0.25s ease, box-shadow 0.25s ease',
        width: '100%',
        '&::before': {
          bgcolor: 'primary.main',
          bottom: 0,
          content: '""',
          left: 0,
          position: 'absolute',
          top: 0,
          transform: 'scaleY(0)',
          transformOrigin: 'top',
          transition: 'transform 0.25s ease',
          width: 3
        },
        '&:focus-visible': {
          borderColor: 'primary.main',
          boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}33`
        },
        '@media (hover: hover)': {
          '&:hover': {
            borderColor: (theme) => theme.palette.mode === 'dark'
              ? theme.palette.primary.light
              : theme.palette.primary.main,
            boxShadow: (theme) => theme.palette.mode === 'dark'
              ? '0 10px 32px rgba(0,0,0,0.4)'
              : '0 10px 32px rgba(15,23,42,0.09)',
            '&::before': {
              transform: 'scaleY(1)'
            },
            '& .product-image': {
              transform: 'scale(1.04)'
            },
            '& .product-cta': {
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              borderColor: 'primary.main',
              '& .cta-label': {
                color: 'primary.main'
              },
              '& .cta-arrow-btn': {
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                transform: 'scale(1.06)'
              }
            }
          }
        },
        ...sx
      }}
      tabIndex={onClick ? 0 : undefined}
      {...other}
    >
      <Box
        className="product-image"
        sx={{
          aspectRatio: '3 / 4',
          backgroundColor: (theme) =>
            theme.palette.mode === 'dark' ? 'neutral.900' : 'neutral.100',
          backgroundImage: image ? `url(${image})` : 'none',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          borderBottom: (theme) => theme.palette.mode === 'dark'
            ? `1px solid ${theme.palette.neutral[700]}`
            : `1px solid ${theme.palette.neutral[200]}`,
          flexShrink: 0,
          overflow: 'hidden',
          transition: 'transform 0.4s ease',
          width: '100%'
        }}
      >
        {!image && (
          <Stack alignItems="center" justifyContent="center" sx={{ color: 'text.disabled', height: '100%' }}>
            <ImageOutlinedIcon sx={{ fontSize: 40, opacity: 0.35 }} />
          </Stack>
        )}
      </Box>

      <Stack spacing={{ xs: 0.75, sm: 1.25 }} sx={{ flexGrow: 1, minWidth: 0, p: { xs: 1, sm: 1.5 } }}>
        <Stack alignItems="flex-start" direction="row" justifyContent="space-between" spacing={1}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {displayCode && (
              <Typography
                color="text.secondary"
                noWrap
                sx={{ fontSize: '0.6875rem', fontWeight: 500, mb: 0.35 }}
                variant="caption"
              >
                {displayCode}
              </Typography>
            )}
            <Typography
              sx={{
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
                display: '-webkit-box',
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                fontWeight: 600,
                lineHeight: 1.4,
                overflow: 'hidden'
              }}
            >
              {name}
            </Typography>
          </Box>

          <Stack alignItems="flex-end" flexShrink={0} spacing={0}>
            <Typography
              color="primary.main"
              sx={{ fontSize: { xs: '0.9375rem', sm: '1rem' }, fontWeight: 800, lineHeight: 1.1 }}
            >
              {price}
            </Typography>
            <Typography color="text.secondary" sx={{ fontSize: '0.6875rem', fontWeight: 500 }}>
              {currency}
            </Typography>
          </Stack>
        </Stack>

        <Box
          className="product-cta"
          component="button"
          onClick={(e) => {
            e.stopPropagation();
            onClick?.();
          }}
          sx={{
            alignItems: 'center',
            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'neutral.900' : 'neutral.50',
            border: (theme) => theme.palette.mode === 'dark'
              ? `1px solid ${theme.palette.neutral[600]}`
              : `1px solid ${theme.palette.neutral[300]}`,
            borderRadius: 1.5,
            cursor: 'pointer',
            display: 'flex',
            font: 'inherit',
            justifyContent: 'space-between',
            minHeight: { xs: 34, sm: 38 },
            mt: 'auto',
            outline: 'none',
            px: { xs: 1, sm: 1.25 },
            py: { xs: 0.4, sm: 0.55 },
            textAlign: 'left',
            transition: 'background-color 0.2s ease, border-color 0.2s ease',
            width: '100%',
            '&:focus-visible': {
              borderColor: 'primary.main',
              boxShadow: (theme) => `0 0 0 2px ${alpha(theme.palette.primary.main, 0.25)}`
            },
            '&:hover': {
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              borderColor: 'primary.main',
              '& .cta-label': {
                color: 'primary.main'
              },
              '& .cta-arrow-btn': {
                bgcolor: 'primary.main',
                color: 'primary.contrastText'
              }
            },
            '&:active': {
              transform: 'scale(0.99)'
            }
          }}
          type="button"
        >
          <Typography
            className="cta-label"
            sx={{
              color: 'text.primary',
              fontSize: { xs: '0.75rem', sm: '0.8125rem' },
              fontWeight: 600,
              lineHeight: 1.2,
              transition: 'color 0.2s ease'
            }}
          >
            {cta}
          </Typography>
          <Box
            className="cta-arrow-btn"
            sx={{
              alignItems: 'center',
              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'neutral.800' : 'common.white',
              border: (theme) => theme.palette.mode === 'dark'
                ? `1px solid ${theme.palette.neutral[600]}`
                : `1px solid ${theme.palette.neutral[200]}`,
              borderRadius: '50%',
              color: 'text.primary',
              display: 'flex',
              flexShrink: 0,
              height: { xs: 26, sm: 28 },
              justifyContent: 'center',
              ml: 0.75,
              transition: 'background-color 0.2s ease, color 0.2s ease, transform 0.2s ease, border-color 0.2s ease',
              width: { xs: 26, sm: 28 }
            }}
          >
            <ArrowForwardIcon sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }} />
          </Box>
        </Box>
      </Stack>
    </Card>
  );
};

PricingPlan.propTypes = {
  cta: propTypes.string.isRequired,
  currency: propTypes.string.isRequired,
  code: propTypes.string,
  description: propTypes.string,
  features: propTypes.array,
  icon: propTypes.any,
  image: propTypes.string,
  name: propTypes.string.isRequired,
  popular: propTypes.bool,
  price: propTypes.string.isRequired,
  onClick: propTypes.func,
  sx: propTypes.object
};
